import React from "react";
import type { GetStaticPropsContext, NextPage } from "next";
import Head from "next/head";
import Image from "next/image";
import { Box, styled } from "@mui/material";

import WsPhantomNav from "@components/a1-refactor/typography/WsPhantomNav";

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`@lang/${locale}.json`)).default,
    },
  };
}

const CocktailThankYouPage: NextPage = () => {
  return (
    <>
      <Head>
        <title>Cóctel Construyendo 2026 | RE/MAX República Dominicana</title>
        <meta
          name="description"
          content="Tu asistencia al Cóctel Construyendo 2026 de RE/MAX República Dominicana ha sido confirmada."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <WsPhantomNav />

      <PageSection>
        <ContentCard>
          <LogoWrapper>
            <Image
              src="/images/v2/logos/8.png"
              alt="RE/MAX República Dominicana"
              width={2880}
              height={768}
              priority
            />
          </LogoWrapper>

          <EventName>Cóctel Construyendo 2026</EventName>

          <Title>¡Tu asistencia ha sido confirmada!</Title>

          <Message>
            Gracias por confirmar tu participación en Construyendo 2026.
          </Message>

          <Message>
            Nos alegra contar contigo en esta noche de conexiones,
            reconocimiento y nuevas oportunidades.
          </Message>

          <Message>
            <strong>¡Nos vemos pronto!</strong>
          </Message>

          <LearnMoreButton href="https://remaxrd.com">
            VISITAR REMAX REP. DOM
          </LearnMoreButton>
        </ContentCard>
      </PageSection>
    </>
  );
};

export default CocktailThankYouPage;

const PageSection = styled("section")(() => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "calc(100vh - 73px)",
  padding: "64px 24px",
  overflow: "hidden",
  background:
    "radial-gradient(circle at 12% 18%, rgba(0, 67, 255, 0.11), transparent 28%), radial-gradient(circle at 88% 82%, rgba(255, 18, 0, 0.09), transparent 28%), #f7f8fc",
  "@media (max-width: 600px)": {
    minHeight: "calc(100svh - 73px)",
    padding: "40px 16px",
  },
}));

const ContentCard = styled(Box)(() => ({
  position: "relative",
  width: "100%",
  maxWidth: 720,
  padding: "64px 72px",
  border: "1px solid rgba(0, 14, 53, 0.08)",
  borderRadius: 24,
  backgroundColor: "#fff",
  boxShadow: "0 24px 70px rgba(0, 14, 53, 0.1)",
  textAlign: "center",
  "@media (max-width: 600px)": {
    padding: "44px 24px",
    borderRadius: 18,
  },
}));

const LogoWrapper = styled(Box)(() => ({
  width: "min(360px, 78vw)",
  margin: "0 auto 30px",
  "& img": {
    display: "block",
    width: "100%",
    height: "auto",
    transform: "translateX(13%)",
  },
}));

const EventName = styled("p")(() => ({
  marginBottom: 14,
  color: "var(--color-brand-blue-secondary)",
  fontSize: "0.875rem",
  fontWeight: 700,
  letterSpacing: "0.13em",
  lineHeight: 1.4,
  textTransform: "uppercase",
}));

const Title = styled("h1")(() => ({
  marginBottom: 20,
  color: "var(--color-brand-blue-main)",
  fontSize: "clamp(2rem, 5vw, 3rem)",
  fontWeight: 700,
  lineHeight: 1.12,
}));

const Message = styled("p")(() => ({
  maxWidth: 550,
  margin: "0 auto 10px",
  color: "#4f566b",
  fontSize: "clamp(1rem, 2vw, 1.125rem)",
  lineHeight: 1.7,
}));

const LearnMoreButton = styled("a")(() => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 52,
  marginTop: 28,
  padding: "14px 30px",
  borderRadius: 999,
  backgroundColor: "var(--color-brand-blue-secondary)",
  boxShadow: "0 12px 28px rgba(0, 67, 255, 0.24)",
  color: "#fff",
  fontSize: "1rem",
  fontWeight: 700,
  lineHeight: 1.2,
  transition: "transform 180ms ease, box-shadow 180ms ease",
  "&:hover": {
    boxShadow: "0 16px 34px rgba(0, 67, 255, 0.3)",
    color: "#fff",
    transform: "translateY(-2px)",
  },
  "&:focus-visible": {
    outline: "3px solid rgba(0, 67, 255, 0.32)",
    outlineOffset: 4,
  },
  "@media (max-width: 480px)": {
    width: "100%",
  },
}));
